import {Injectable} from '@angular/core';
import {
  HttpRequest,
  HttpHandler,
  HttpInterceptor
} from '@angular/common/http';
import {Observable, throwError} from 'rxjs';
import {catchError, filter, mergeMap, take} from 'rxjs/operators';
import {RoutingService} from '@app/service/routing/routing.service';
import {interceptorConstants} from '@shared/interfaces';
import {authState, getAuth, User} from '@angular/fire/auth';
import {environment} from '@env';
import {from} from 'rxjs';

@Injectable()
export class TokenInterceptor implements HttpInterceptor {
  errorCount = 0;

  constructor(private routingService: RoutingService) {
  }

  /**
   * For every request, add a bearer token for server side authentication and handle errors that are returned by API
   * @param request
   * @param next
   */
  intercept(request: HttpRequest<any>, next: HttpHandler): Observable<any> {
    const auth = getAuth();
    const currentUser = auth.currentUser;
    const requestUrl = request.url || '';
    const mlUtilitiesUrl = environment.mlUtilitiesUrl;
    const needsMlUtilsAuth = mlUtilitiesUrl && requestUrl.startsWith(mlUtilitiesUrl);

    if (!currentUser) {
      // For ml-utilities requests we must have a token (backend enforces it for /v1/api/*).
      // Wait for the first user (including anonymous) instead of sending unauthenticated.
      if (needsMlUtilsAuth) {
        return authState(auth)
          .pipe(
            filter((user: User | null) => !!user),
            take(1),
            mergeMap((user: User) => from(user.getIdToken())),
            mergeMap((token: string) => this.handleWithToken(request, next, token))
          );
      }
      return next.handle(request);
    }
    // Get Auth Token that is saved for a user and make sure the request is to project specific api
    return from(currentUser.getIdToken())
      .pipe(mergeMap((token: string) => this.handleWithToken(request, next, token)));
  }

  private handleWithToken(request: HttpRequest<any>, next: HttpHandler, token: string): Observable<any> {
    if (!token) {
      return next.handle(request);
    }
    let authReq = request.clone({
      setHeaders: {
        Authorization: `Bearer ${token}`,
        'Content-Type': 'application/json',
        'Access-Control-Allow-Origin': '*'
      }
    });
    if (authReq.body) {
      authReq = authReq.clone({
        body: {...authReq.body}
      });
    }

    return next.handle(authReq).pipe(catchError((error) => {
      if (error.status === interceptorConstants.UNAUTHORIZED) {
        // logout users, redirect to login page
        // this.store.dispatch(new Logout());
      }

      if (error.status === interceptorConstants.PRECONDITION_FAILED) {
        location.reload();
      }

      if (error.status === interceptorConstants.INTERNAL_ERROR || error.status === interceptorConstants.GATEWAY_TIMEOUT) {
        if (this.errorCount < interceptorConstants.MAXIMUM_RE_REQUEST_LIMIT) {
          this.errorCount++;
          return next.handle(authReq);
        } else {
          this.routingService.navigateHome();
        }
      }

      return throwError(error);
    })) as any;
  }
}
